import type { Attachment } from "@/lib/types";
import { isVideoFile } from "@/lib/media/video";
import {
  attachmentKindToCmsMediaKind,
  type CmsMediaKind,
} from "@/lib/cms/cms-media-labels";

const KIND_SUFFIX: Record<CmsMediaKind, string> = {
  image: "Image",
  video: "Video",
  embed: "Embed",
};

export type MediaLibraryLabelKey =
  | "button"
  | "buttonEdit"
  | "title"
  | "titleEdit"
  | "submit"
  | "submitBatch"
  | "save"
  | "uploadSuccess"
  | "uploadSuccessBatch"
  | "updateSuccessTitle"
  | "updateSuccessDescription"
  | "updateErrorTitle"
  | "updateErrorDescription"
  | "deleteSuccessTitle"
  | "deleteSuccessDescription"
  | "uploadError";

export type MediaLibraryFormLabelKey =
  | "caption"
  | "textAlt"
  | "name"
  | "publishedLabel"
  | "publishedLabelPlural"
  | "publishedPlaceholder"
  | "publishedYes"
  | "publishedYesPlural"
  | "publishedNo"
  | "publishedNoPlural"
  | "photoCredits";

const IMAGE_FORM_LABEL_KEYS: Record<MediaLibraryFormLabelKey, string> = {
  caption: "attachment.form.caption",
  textAlt: "attachment.form.textAlt",
  name: "attachment.form.name",
  publishedLabel: "imageFieldPublishedLabel",
  publishedLabelPlural: "imageFieldPublishedLabel_plural",
  publishedPlaceholder: "imageFieldPublishedPlaceholder",
  publishedYes: "imageFieldPublished",
  publishedYesPlural: "imageFieldPublished_plural",
  publishedNo: "imageFieldNotPublished",
  publishedNoPlural: "imageFieldNotPublished_plural",
  photoCredits: "attachment.form.photoCredits",
};

/** Drawer form field labels — image keeps legacy keys; video/embed use mediaLibrary.add.* */
export function mediaLibraryFormTranslationKey(
  label: MediaLibraryFormLabelKey,
  kind: CmsMediaKind,
): string {
  if (kind === "image" || label === "caption" || label === "textAlt") {
    return IMAGE_FORM_LABEL_KEYS[label];
  }
  const suffix = KIND_SUFFIX[kind];
  const fieldKeys: Record<MediaLibraryFormLabelKey, string> = {
    caption: `caption${suffix}`,
    textAlt: `textAlt${suffix}`,
    name: `name${suffix}`,
    publishedLabel: `publishedLabel${suffix}`,
    publishedLabelPlural: `publishedLabelPlural${suffix}`,
    publishedPlaceholder: `publishedPlaceholder${suffix}`,
    publishedYes: `publishedYes${suffix}`,
    publishedYesPlural: `publishedYesPlural${suffix}`,
    publishedNo: `publishedNo${suffix}`,
    publishedNoPlural: `publishedNoPlural${suffix}`,
    photoCredits: `photoCredits${suffix}`,
  };
  return `mediaLibrary.add.${fieldKeys[label]}`;
}

/** Artwork media-library drawer labels — filled by the client in the translation DB. */
export function mediaLibraryTranslationKey(
  label: MediaLibraryLabelKey,
  kind: CmsMediaKind,
): string {
  const suffix = KIND_SUFFIX[kind];
  const keys: Record<MediaLibraryLabelKey, string> = {
    button: `mediaLibrary.add.button${suffix}`,
    buttonEdit: `mediaLibrary.add.buttonEdit${suffix}`,
    title: `mediaLibrary.add.title${suffix}`,
    titleEdit: `mediaLibrary.add.titleEdit${suffix}`,
    submit: `mediaLibrary.add.submit${suffix}`,
    submitBatch: `mediaLibrary.add.submitBatch${suffix}`,
    save: `mediaLibrary.add.save${suffix}`,
    uploadSuccess: `mediaLibrary.add.uploadSuccess${suffix}`,
    uploadSuccessBatch: `mediaLibrary.add.uploadSuccessBatch${suffix}`,
    updateSuccessTitle: `mediaLibrary.add.updateSuccessTitle${suffix}`,
    updateSuccessDescription: `mediaLibrary.add.updateSuccessDescription${suffix}`,
    updateErrorTitle: `mediaLibrary.add.updateErrorTitle${suffix}`,
    updateErrorDescription: `mediaLibrary.add.updateErrorDescription${suffix}`,
    deleteSuccessTitle: `mediaLibrary.add.deleteSuccessTitle${suffix}`,
    deleteSuccessDescription: `mediaLibrary.add.deleteSuccessDescription${suffix}`,
    uploadError: `mediaLibrary.add.uploadError${suffix}`,
  };
  return keys[label];
}

export function resolveMediaLibraryKind(args: {
  attachment?: Attachment | null;
  files?: File[];
}): CmsMediaKind {
  if (args.attachment) {
    return attachmentKindToCmsMediaKind(args.attachment.kind);
  }
  const files = args.files ?? [];
  if (files.length > 0 && files.every((file) => isVideoFile(file))) {
    return "video";
  }
  return "image";
}
